/* ==========================================================================
   Box rewards — Slime Sculptor
   - <box-rewards>  tiered progress bar (gift / -10% / free shipping / -15%)
   - <box-upsell>   quick-add rail with per-product counters
   - <box-batch>    "next batch ships on <weekday>, <date>"
   - Auto-adds / removes the €0 gift (activator) line with property _box_gift
   - Any line with _gift (welcome gift from free-gift.liquid) or _box_gift never counts toward the box
   Works with Dawn's pubsub.js (PUB_SUB_EVENTS.cartUpdate) and <cart-drawer>.
   Load AFTER pubsub.js:  <script src="{{ 'box-rewards.js' | asset_url }}" defer></script>
   ========================================================================== */
(() => {
  if (customElements.get('box-rewards')) return;

  const R = window.routes || {};
  const URL_CART = (R.cart_url || '/cart') + '.js';
  const URL_ADD = (R.cart_add_url || '/cart/add') + '.js';
  const URL_CHANGE = (R.cart_change_url || '/cart/change') + '.js';
  const SOURCE = 'box-rewards';

  const hasPubSub = () => typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined';
  // Any free line (welcome gift or box activator) — never counts toward the box
  const isGift = (item) => !!(item.properties && (item.properties._gift || item.properties._box_gift));
  // Only the box activator line — the one this script manages
  const isBoxGift = (item) => !!(item.properties && item.properties._box_gift);

  /* ---------- config ---------- */
  let CFG;
  const cfg = () => {
    if (CFG === undefined) {
      const el = document.getElementById('BoxRewardsConfig');
      try { CFG = el ? JSON.parse(el.textContent) : null; } catch (e) { CFG = null; }
    }
    return CFG;
  };

  const money = (cents) => {
    const c = cfg() || {};
    try {
      return new Intl.NumberFormat(c.locale || 'bg-BG', { style: 'currency', currency: c.currency || 'EUR' }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  };
  const fillTpl = (tpl, vars) => (tpl || '').replace(/\{(\w+)\}/g, (m, k) => (k in vars ? vars[k] : m));

  /* ---------- shared cart store ---------- */
  const store = {
    cart: null,
    async load() {
      const res = await fetch(URL_CART, { headers: { Accept: 'application/json' } });
      return res.json();
    },
    set(cart) {
      this.cart = cart;
      document.dispatchEvent(new CustomEvent('box:cart', { detail: cart }));
    },
  };

  /* ---------- reward state ---------- */
  function computeState(cart) {
    const c = cfg();
    const type = (c.productType || '').trim().toLowerCase();
    const counts = (i) => !isGift(i) && (!type || (i.product_type || '').toLowerCase() === type);

    const items = cart.items.filter(counts);
    const count = items.reduce((s, i) => s + i.quantity, 0);
    const boxTotal = items.reduce((s, i) => s + (i.final_line_price ?? i.line_price), 0);
    const subtotal = cart.items_subtotal_price;
    const avg = count ? boxTotal / count : c.fallbackPriceCents;

    const milestones = (c.tiers || [])
      .filter((t) => t.qty > 0 && t.percent > 0)
      .map((t) => ({ kind: 'tier', eq: t.qty, qty: t.qty, percent: t.percent, label: `-${t.percent}%`, reached: count >= t.qty }));

    if (c.freeShippingCents > 0) {
      // Place the free-shipping node by "how many items it takes" at the current average price.
      let eq;
      if (avg) eq = c.freeShippingCents / avg;
      else {
        const qs = milestones.map((m) => m.eq);
        eq = qs.length ? (Math.min(...qs) + Math.max(...qs)) / 2 : 3;
      }
      milestones.push({ kind: 'shipping', eq, label: c.shippingLabel, reached: subtotal >= c.freeShippingCents });
    }
    milestones.sort((a, b) => a.eq - b.eq);

    // Even spacing, last node at 92% so the track runs on a little (like the reference).
    const N = milestones.length;
    // Nodes evenly spaced from 18% to 84%, so every label (incl. the last) stays centred under its circle
    const FIRST = 18, LAST = 84;
    milestones.forEach((m, i) => { m.pos = N === 1 ? LAST : FIRST + ((LAST - FIRST) * i) / (N - 1); });

    let pct = 0, prevEq = 0, prevPos = 0;
    for (const m of milestones) {
      if (m.reached) { prevEq = m.eq; prevPos = m.pos; pct = m.pos; continue; }
      const span = Math.max(m.eq - prevEq, 0.0001);
      pct = prevPos + (m.pos - prevPos) * Math.min(1, Math.max(0, (count - prevEq) / span));
      break;
    }
    if (N && milestones.every((m) => m.reached)) pct = 100;

    const goals = [...milestones];
    if (c.giftVariantId && c.giftMinQty > 0) goals.push({ kind: 'gift', eq: c.giftMinQty, reached: count >= c.giftMinQty });
    const next = goals.filter((g) => !g.reached).sort((a, b) => a.eq - b.eq)[0];
    const nextMilestone = milestones.find((m) => !m.reached);

    let message;
    if (!next) message = c.msgDone;
    else if (next.kind === 'gift') message = fillTpl(c.msgGift, { n: c.giftMinQty - count });
    else if (next.kind === 'tier') message = fillTpl(c.msgTier, { n: next.qty - count, p: next.percent });
    else message = fillTpl(c.msgShipping, { amount: money(c.freeShippingCents - subtotal) });

    return {
      count, subtotal, milestones, pct, message, nextMilestone,
      giftWanted: !!c.giftVariantId && c.giftMinQty > 0 && count >= c.giftMinQty,
    };
  }

  /* ---------- cart requests ---------- */
  function renderSections(sections, html) {
    sections.forEach((s) => {
      const markup = html[s.id];
      if (!markup) return;
      const target = s.selector ? document.querySelector(s.selector) : document.getElementById(s.id);
      if (!target) return;
      const doc = new DOMParser().parseFromString(markup, 'text/html');
      const src = doc.querySelector(s.selector || '.shopify-section');
      if (src) target.innerHTML = src.innerHTML;
    });
  }

  // Dawn shows the "cart is empty" screen only when <cart-drawer> has the is-empty class
  function syncDrawerEmpty(itemCount) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.toggle('is-empty', itemCount === 0);
  }

  async function cartRequest(url, body) {
    const drawer = document.querySelector('cart-drawer');
    const sections = drawer && typeof drawer.getSectionsToRender === 'function' ? drawer.getSectionsToRender() : [];
    if (sections.length) {
      body.sections = sections.map((s) => s.id);
      body.sections_url = window.location.pathname;
    }
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Cart request failed');

    if (data.sections) {
      renderSections(sections, data.sections);
      // /cart/change returns the whole cart (item_count); /cart/add returns only the added items → cart isn't empty
      syncDrawerEmpty(typeof data.item_count === 'number' ? data.item_count : 1);
    }
    if (hasPubSub()) publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: data });
    return data;
  }

  /* ---------- gift sync ---------- */
  let syncing = false;
  async function syncGift(cart) {
    const c = cfg();
    if (!c || !c.giftVariantId || syncing) return false;

    const { giftWanted } = computeState(cart);
    const gifts = cart.items.filter(isBoxGift);
    let req = null;

    if (giftWanted && !gifts.length) {
      req = [URL_ADD, { items: [{ id: c.giftVariantId, quantity: 1, properties: { _box_gift: '1' } }] }];
    } else if (!giftWanted && gifts.length) {
      req = [URL_CHANGE, { id: gifts[0].key, quantity: 0 }];
    } else if (giftWanted && gifts.length > 1) {
      req = [URL_CHANGE, { id: gifts[gifts.length - 1].key, quantity: 0 }];
    } else if (giftWanted && gifts[0].quantity !== 1) {
      req = [URL_CHANGE, { id: gifts[0].key, quantity: 1 }];
    }
    if (!req) return false;

    syncing = true;
    try {
      await cartRequest(req[0], req[1]);
      // Cart page has no drawer to re-render, so reload it to show the gift line.
      if (!document.querySelector('cart-drawer') && document.querySelector('cart-items')) window.location.reload();
      return true;
    } catch (e) {
      console.warn('[box-rewards] gift sync failed:', e.message);
      return false;
    } finally {
      syncing = false;
    }
  }

  /* ---------- refresh loop (debounced) ---------- */
  let timer;
  function refresh() {
    if (!cfg()) return;
    clearTimeout(timer);
    timer = setTimeout(async () => {
      try {
        let cart = await store.load();
        store.set(cart);
        for (let i = 0; i < 3 && (await syncGift(cart)); i++) {
          cart = await store.load();
          store.set(cart);
        }
        // Only ever mark empty here; un-marking is left to whoever re-renders the drawer with items
        if (cart.item_count === 0) syncDrawerEmpty(0);
      } catch (e) {
        console.warn('[box-rewards]', e);
      }
    }, 120);
  }

  /* ---------- "Добави още" label on the product form button ---------- */
  function updateCta(cart) {
    document.querySelectorAll('[data-box-cta]').forEach((label) => {
      const form = label.closest('form');
      const input = (form && form.querySelector('input[name="id"]')) || document.querySelector('form[action*="/cart/add"] input[name="id"]');
      const id = Number(input && input.value);
      const inCart = cart.items.some((i) => i.variant_id === id && !isGift(i));
      const text = inCart ? label.dataset.again : label.dataset.first;
      if (text && label.textContent !== text) label.textContent = text;
    });
  }

  /* ---------- <box-rewards> ---------- */
  class BoxRewards extends HTMLElement {
    connectedCallback() {
      if (!cfg()) return;
      this.onCart = (e) => this.render(e.detail);
      document.addEventListener('box:cart', this.onCart);
      let initial = null;
      try { initial = JSON.parse(this.querySelector('[data-box-cart]')?.textContent || 'null'); } catch (e) {}
      this.render(initial || store.cart);
    }
    disconnectedCallback() {
      document.removeEventListener('box:cart', this.onCart);
    }
    build(milestones, key) {
      const track = this.querySelector('[data-box-track]');
      const labels = this.querySelector('[data-box-labels]');
      track.querySelectorAll('.box-rewards__node').forEach((n) => n.remove());
      labels.innerHTML = '';
      milestones.forEach((m) => {
        const node = document.createElement('span');
        node.className = 'box-rewards__node';
        track.appendChild(node);
        const lab = document.createElement('span');
        lab.className = 'box-rewards__label';
        lab.textContent = m.label;
        labels.appendChild(lab);
      });
      this.builtKey = key;
    }
    render(cart) {
      if (!cart || !cart.items) return;
      const s = computeState(cart);
      if (this.dataset.context === 'drawer') this.hidden = s.count === 0;

      const key = s.milestones.map((m) => m.kind + m.label).join('|');
      if (key !== this.builtKey) this.build(s.milestones, key);

      const nodes = this.querySelectorAll('.box-rewards__node');
      const labs = this.querySelectorAll('.box-rewards__label');
      s.milestones.forEach((m, i) => {
        nodes[i].style.left = labs[i].style.left = `${m.pos}%`;
        const next = m === s.nextMilestone;
        nodes[i].classList.toggle('is-reached', m.reached);
        labs[i].classList.toggle('is-reached', m.reached);
        labs[i].classList.toggle('is-next', next);
      });
      this.querySelector('[data-box-fill]').style.width = `${s.pct}%`;
      const msg = this.querySelector('[data-box-msg]');
      if (msg.innerHTML !== s.message) msg.innerHTML = s.message;

      updateCta(cart);
    }
  }

  /* ---------- <box-upsell> ---------- */
  class BoxUpsell extends HTMLElement {
    connectedCallback() {
      this.onCart = (e) => this.sync(e.detail);
      document.addEventListener('box:cart', this.onCart);
      if (store.cart) this.sync(store.cart);
      this.onClick = (e) => {
        const add = e.target.closest('button[data-box-add]');
        if (add) { e.preventDefault(); this.add(add); return; }
        const nav = e.target.closest('[data-box-scroll]');
        if (nav) {
          const rail = this.querySelector('[data-box-rail]');
          rail.scrollBy({ left: Number(nav.dataset.boxScroll) * rail.clientWidth * 0.8, behavior: 'smooth' });
        }
      };
      this.addEventListener('click', this.onClick);
    }
    disconnectedCallback() {
      document.removeEventListener('box:cart', this.onCart);
      this.removeEventListener('click', this.onClick);
    }
    sync(cart) {
      this.querySelectorAll('button[data-box-add]').forEach((btn) => {
        const id = Number(btn.dataset.boxAdd);
        const qty = cart.items.filter((i) => i.variant_id === id && !isGift(i)).reduce((s, i) => s + i.quantity, 0);
        btn.classList.toggle('has-qty', qty > 0);
        const badge = btn.querySelector('[data-box-count]');
        if (badge) badge.textContent = qty;
      });
    }
    async add(btn) {
      if (btn.disabled) return;
      btn.disabled = true;
      btn.classList.add('is-loading');
      try {
        await cartRequest(URL_ADD, { items: [{ id: Number(btn.dataset.boxAdd), quantity: 1 }] });
        refresh();
      } catch (e) {
        console.warn('[box-upsell]', e.message);
      } finally {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      }
    }
  }

  /* ---------- <box-batch> ---------- */
  class BoxBatch extends HTMLElement {
    connectedCallback() {
      const weekday = Number(this.dataset.weekday); // 0 = Sunday … 6 = Saturday
      const d = new Date();
      const diff = ((weekday - d.getDay() + 7) % 7) || 7; // always the NEXT one, never today
      d.setDate(d.getDate() + diff);
      const c = cfg() || {};
      let text;
      try {
        text = new Intl.DateTimeFormat(c.locale || 'bg-BG', { weekday: 'long', day: 'numeric', month: 'long' }).format(d);
      } catch (e) {
        text = d.toDateString();
      }
      this.querySelectorAll('[data-box-batch-date]').forEach((el) => { el.textContent = text; });
    }
  }

  customElements.define('box-rewards', BoxRewards);
  customElements.define('box-upsell', BoxUpsell);
  customElements.define('box-batch', BoxBatch);

  /* ---------- listen for cart changes ---------- */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (e) => {
      if (!e || e.source !== SOURCE) refresh();
    });
  }
  document.addEventListener('box:refresh', refresh);
  window.addEventListener('pageshow', (e) => { if (e.persisted) refresh(); });
  refresh();
})();